// Fly's local tool — Add section — reuses the workspace's board vocabulary and
// board renderer. It never asks a model, fetches data, or copies app
// definitions into session state. Every export is self-contained for Frank
// serialization.
//
// ONE TOOL, IN THE COMPOSER'S "+" (Nika 2026-10-23: "I'm not sure about these
// buttons … maybe we move them somewhere else? open workspace tools is already
// in top menu so whats the point?"): the canvas no longer carries a toolbar.
// "Open workspace" is gone — the top menu and the search open every app.
// "Add section" is a row of the composer's "+" menu while the canvas is on
// screen (assistantFlySectionOffered; canvas CanvasPromptTextarea AttachMenu
// FlySectionRow) — the composer is where a Fly page is made, asked or built.
// What stays here: the section picker, its "Section added" note in the
// canvas's bar, and the guarded return to Fly over an open pane.
//
// The sentence of a failure code — every key written out, so the i18n gates
// ("every shipped key is referenced") see each one; an unknown code reads as
// the navigation failure.
export function assistantFlyWorkspaceErrorKey(code) {
  const KEYS = {
    unavailable: 'shell.flyWorkspace.error.unavailable',
    limit: 'shell.flyWorkspace.error.limit',
    duplicate: 'shell.flyWorkspace.error.duplicate',
    navigation: 'shell.flyWorkspace.error.navigation',
    changed: 'shell.flyWorkspace.error.changed',
    saving: 'shell.flyWorkspace.error.saving'
  }
  return KEYS[code] || KEYS.navigation
}

export function assistantFlyWorkspaceState(root) {
  const r = root || (this.state && this.state.root)
  const ws = r && r.workspace
  const workspaceId = ws && (ws.id || ws._id)
  if (!workspaceId) return null
  const saved = r._aiFlyWorkspace
  if (saved && String(saved.workspaceId) === String(workspaceId)) return saved
  return {
    workspaceId: String(workspaceId),
    picker: null,
    originPath: null,
    originFocus: null,
    returnPending: false,
    status: 'idle',
    error: null,
    operationId: null,
    seq: 0
  }
}

export function assistantFlyWorkspaceEnter() {
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  if (!state || !root || typeof root.update !== 'function') return false
  const fns = (this.context && this.context.functions) || {}
  let known = this.call('assistantFlySession', root)
  if (!known && typeof fns.assistantFlyRestore === 'function') {
    this.call('assistantFlyRestore', root)
    known = this.call('assistantFlySession', root)
  }
  const fly =
    known && String(known.workspaceId) === state.workspaceId
      ? known
      : { v: 1, workspaceId: state.workspaceId, threadId: null, focus: null, pages: [] }
  const path = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  for (const name of [
    'assistantBoardDismiss',
    'assistantDeckDismiss',
    'assistantMainThreadClose'
  ]) {
    if (typeof fns[name] === 'function') this.call(name)
  }
  root.update(
    {
      _aiFly: { ...fly, open: true, path },
      _aiFlyWorkspace: {
        ...state,
        originPath: path,
        originFocus: fly.focus || null,
        picker: null,
        returnPending: !!(root._canvasSlot || root._canvasSlotSecond),
        status: 'idle',
        error: null,
        operationId: null,
        seq: (state.seq || 0) + 1
      }
    },
    { preventFetch: true }
  )
  // A mode choice must not discard an existing pane. The same guarded return
  // path closes it only after the person accepts the normal unsaved prompt.
  if (root._canvasSlot || root._canvasSlotSecond) return this.call('assistantFlyWorkspaceReturn')
  return true
}

// WHERE FLY CAN RUN: the person can ask here — the workspace's assistant and
// its composer are on (the composer's own first two gates; the phone dock's
// `canCompose` reads the same pair). Fly is the home's default view (Nika
// 2026-10-22: "ok make fly default view"), so this is what keeps a workspace
// that turned its assistant off on its dashboard: no canvas opens there by
// default (assistantFlyWorkspaceMounted), none shows (assistantFlyShown) and
// the home offers no switch (homeViewShown). Both keys are read every time
// (reactive callers).
export function assistantFlyAvailable(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this && this.context && this.context.functions) || {}
  if (typeof fns.featureEnabled !== 'function') return true
  const assistant = !!this.call('featureEnabled', r, 'aiAssistant')
  const composer = !!this.call('featureEnabled', r, 'aiAssistant.composer')
  return assistant && composer
}

// The always-mounted shell picker calls this on workspace, mode, route and
// flag changes. Track an edge on the host, not on shared component scope.
// Closing Fly is deliberate; ordinary root updates must never reopen it or
// repeat a prompt.
//
// FLY IS THE HOME'S DEFAULT VIEW (Nika 2026-10-22: "ok make fly default
// view"): 'fly' is the mode of a person who never chose one (state-app.js,
// functions/assistantMode.js), and in Fly mode the home opens on the canvas —
// at a load on the home, and at the first arrival on the home after a load
// on another route, while the canvas has not been on screen since. A canvas
// the person closed stays closed; one open on another route waits there for
// the next ask, as before.
export function assistantFlyWorkspaceMounted(el, s) {
  const host = el || this
  const root = (s && s.root) || (host.state && host.state.root)
  const ws = root && root.workspace
  const workspaceId = ws && (ws.id || ws._id)
  const mode = root && root.assistantMode
  const fns = (host.context && host.context.functions) || {}
  const path =
    root && typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  const bare =
    typeof fns.stripWorkspacePrefix === 'function' ? host.call('stripWorkspacePrefix', path) : path
  const home = typeof bare === 'string' && bare.replace(/\/+$/, '') === ''
  const available =
    !!root &&
    (typeof fns.assistantFlyAvailable !== 'function' || !!host.call('assistantFlyAvailable', root))
  const previous = host.__flyWorkspaceMountedFor
  const same = !!previous && previous.workspaceId === workspaceId && previous.mode === mode
  let fly = root ? host.call('assistantFlySession', root) : null
  // Fly mode left by anything but the person's own pick (setAssistantMode
  // puts the canvas away for that one): the account's record landing with
  // another mode after the page painted the default. The canvas goes with
  // the mode it opened in.
  if (
    previous &&
    previous.mode === 'fly' &&
    mode !== 'fly' &&
    previous.workspaceId === workspaceId &&
    fly &&
    fly.open
  ) {
    if (typeof fns.assistantFlyWorkspaceDismiss === 'function')
      host.call('assistantFlyWorkspaceDismiss')
    fly = host.call('assistantFlySession', root)
  }
  const open = !!(fly && fly.open)
  // `seen`: the canvas has been on screen in this workspace and mode — from
  // then on only the person, or an ask, opens it again.
  const seen = (same && !!previous.seen) || open
  host.__flyWorkspaceMountedFor = { workspaceId, mode, seen }
  if (!workspaceId || mode !== 'fly' || !available) return false
  if (same) {
    // The same workspace and mode: only the first arrival on the home still
    // opens the canvas, and never over an open pane (an arrival asks nothing).
    if (seen || !home || root._canvasSlot || root._canvasSlotSecond) return false
  } else {
    const state = root._aiFlyWorkspace
    if (open && state && String(state.workspaceId) === String(workspaceId)) return false
    // A load in Fly mode opens the canvas only on the workspace home: a
    // reload or a deep link to another route (/calendar, /crm) keeps that
    // page, and the canvas waits for the home or the next ask (Nika
    // 2026-10-18; QA saw a closed canvas come back over /calendar on every
    // reload). Choosing Fly in the mode menu still opens it anywhere —
    // setAssistantMode enters by itself.
    if (!home) return false
  }
  const entered = host.call('assistantFlyWorkspaceEnter')
  if (entered) host.__flyWorkspaceMountedFor = { workspaceId, mode, seen: true }
  return entered
}

// WHERE "ADD SECTION" IS OFFERED — the composer's "+" menu asks this when it
// opens: in Fly mode, with the canvas on screen (the home, or the layer over
// the route it opened on), in a workspace. Anywhere else the new section would
// land on a page nobody sees, so the row is not there. A pane over the canvas
// (a PageSlot sheet — the composer then speaks for that page) hides it too:
// seen live on dev 2026-10-23, Team members open over the home still offered
// the row.
export function assistantFlySectionOffered(root) {
  const r = root && typeof root === 'object' ? root : (this.state && this.state.root) || {}
  if (r.assistantMode !== 'fly') return false
  if (r._canvasSlot || r._canvasSlotSecond) return false
  if (!this.call('assistantFlyWorkspaceState', r)) return false
  return !!this.call('assistantFlyShown', r)
}

// The section picker (the dialog in components/AssistantFlyWorkspace.js). The
// only picker: `kind` names it so the dialog reads one state shape.
export function assistantFlyWorkspacePicker(kind) {
  if (kind !== 'section') return false
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  if (!state || !root || root.assistantMode !== 'fly' || typeof root.update !== 'function')
    return false
  root.update(
    { _aiFlyWorkspace: { ...state, picker: kind, status: 'idle', error: null } },
    { preventFetch: true }
  )
  return true
}

export function assistantFlyWorkspaceClose() {
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  if (!state || !root || typeof root.update !== 'function') return false
  root.update(
    {
      _aiFlyWorkspace: {
        ...state,
        picker: null,
        status: 'idle',
        error: null,
        operationId: null,
        seq: (state.seq || 0) + 1
      }
    },
    { preventFetch: true }
  )
  return true
}

export function assistantFlyWorkspaceNew() {
  const root = this.state && this.state.root
  if (!root || root.assistantMode !== 'fly' || typeof root.update !== 'function') return false
  this.call('assistantFlyNew')
  return this.call('assistantFlyWorkspaceEnter')
}

// THE HOME AFFORDANCES OPEN A FRESH FLY (Nika 2026-10-23: "clicking logo or
// home icon should be opening fresh fly screen"): the header's logo and home
// icon, the phone dock's Home and `g h`. In Fly mode, where the person can
// ask, they open the home on a NEW session — the start screen — the way the
// rail's New does (the last session stays in the chat history); outside Fly
// mode they go home as before. The session is made ON the home (path '/')
// before the route changes, so the home arrives with the start screen (the
// router may first wait for the leaving page's exit motion). An open pane
// keeps its own guarded close: the affordance then only goes home.
// `opts.navigate === false`: the caller navigates itself (the phone dock).
export function assistantFlyHomeFresh(opts) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const fns = (this.context && this.context.functions) || {}
  const path = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  const bare =
    typeof fns.stripWorkspacePrefix === 'function' ? this.call('stripWorkspacePrefix', path) : path
  const home = typeof bare === 'string' && bare.split(/[?#]/)[0].replace(/\/+$/, '') === ''
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const pane = !!(root._canvasSlot || root._canvasSlotSecond)
  const fly =
    !!workspaceId &&
    !pane &&
    root.assistantMode === 'fly' &&
    (typeof fns.assistantFlyAvailable !== 'function' || !!this.call('assistantFlyAvailable', root))
  if (fly) {
    this.call('assistantFlyNew')
    this.call('assistantFlyWorkspaceEnter')
    if (!home) {
      const fresh = this.call('assistantFlySession', root)
      const state = root._aiFlyWorkspace
      root.update(
        {
          _aiFly: fresh ? { ...fresh, open: true, path: '/' } : root._aiFly,
          _aiFlyWorkspace:
            state && String(state.workspaceId) === String(workspaceId)
              ? { ...state, originPath: '/' }
              : state
        },
        { preventFetch: true }
      )
    }
  }
  if (!home && !(opts && opts.navigate === false) && typeof this.router === 'function') {
    this.router('/', typeof this.getRoot === 'function' ? this.getRoot() : this)
  }
  return true
}

// The header logo's click: a link's (a modified or middle click keeps the
// browser's own new tab), then the fresh home above.
export function assistantFlyHomeClick(e) {
  if (
    e &&
    (e.defaultPrevented ||
      (typeof e.button === 'number' && e.button !== 0) ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey)
  )
    return false
  if (e && typeof e.preventDefault === 'function') e.preventDefault()
  return this.call('assistantFlyHomeFresh')
}

export function assistantFlyWorkspaceDismiss(opts) {
  this.call('assistantFlyWorkspaceClose')
  return this.call('assistantFlyClose', opts)
}

// The sections on offer, derived on demand from the live board vocabulary. A
// value is only an opaque catalog reference; selection resolves it again
// against the current workspace and permissions.
export function assistantFlyWorkspaceOptions(root) {
  const state = this.call('assistantFlyWorkspaceState', root)
  if (!state) return []
  const text = (value) => this.call('polyglot', typeof value === 'string' ? value : '')
  const options = []
  const seen = new Set()
  const vocab = this.call('boardVocabularyFor', root)
  const entries = (vocab && vocab.widgets) || {}
  for (const entry of Object.values(entries)) {
    if (!entry || typeof entry.ref !== 'string' || seen.has(entry.ref)) continue
    const result = this.call(
      'validateBoardSpec',
      { v: 1, items: [{ kind: 'widget', widget: entry.ref, params: {} }] },
      vocab
    )
    // Required parameters without a valid default need an app-specific flow.
    // They cannot truthfully appear as one-click local sections.
    if (!result || result.dropped || result.items.length !== 1) continue
    const app = vocab.apps && vocab.apps[entry.appId]
    seen.add(entry.ref)
    options.push({
      value: 'section:' + entry.ref,
      label: text(entry.title || entry.widgetId),
      // The section's app by its App Store name (installedAppName with the
      // vocabulary's `appKey` — Nika 2026-10-23: "app names = what a person
      // sees in the workspace App Store"), else the widget's own description.
      hint:
        app && app.label
          ? String(
              this.call('installedAppName', { appKey: app.appKey, label: app.label }) ||
                text(app.label)
            )
          : text(entry.describe),
      icon: entry.icon || 'star'
    })
  }
  return options
}

export function assistantFlyWorkspaceSelect(value) {
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  if (!state || !root || root.assistantMode !== 'fly' || typeof root.update !== 'function')
    return false
  const fail = (error) => {
    root.update({ _aiFlyWorkspace: { ...state, status: 'failed', error } }, { preventFetch: true })
    return false
  }
  if (state.picker !== 'section') return false
  const option = this.call('assistantFlyWorkspaceOptions', root).find(
    (entry) => entry.value === value
  )
  if (!option) return fail('unavailable')
  const fly = this.call('assistantFlySession', root)
  if (!fly || String(fly.workspaceId) !== state.workspaceId) return fail('unavailable')

  // All manual sections live on one ready local page. A streaming page's
  // indexed item frames can never overwrite the person's manual insertion.
  const pages = Array.isArray(fly.pages) ? fly.pages : []
  const page = pages.find((item) => item && /^fwl-/.test(item.key) && item.status === 'ready')
  if (page && page.save === 'saving') return fail('saving')
  if ((!page && pages.length >= 20) || (page && (page.items || []).length >= 12))
    return fail('limit')
  const item = { kind: 'widget', widget: option.value.slice(8), params: {} }
  const vocab = this.call('boardVocabularyFor', root)
  const check = this.call('validateBoardSpec', { v: 1, items: [item] }, vocab)
  if (!check || check.dropped || check.items.length !== 1) return fail('unavailable')
  const valid = check.items[0]
  const before = this.call('validateBoardSpec', { v: 1, items: (page && page.items) || [] }, vocab)
  if (before.items.some((entry) => entry.key === valid.key)) return fail('duplicate')
  const raw = { kind: 'widget', widget: valid.ref, size: valid.size, params: valid.params }
  const derived = this.call('boardItemsFor', { v: 1, items: [raw] }, root)
  if (!derived || derived.dropped || derived.items.length !== 1 || !derived.items[0].body)
    return fail('unavailable')
  const key = page ? page.key : 'fwl-' + Date.now().toString(36) + '-' + (state.seq || 0)
  const local = {
    ...(page || {
      key,
      id: null,
      status: 'ready',
      phase: 'done',
      error: null,
      prompt: '',
      eyebrow: '',
      title: this.call('polyglot', 'shell.flyWorkspace.localTitle'),
      opening: '',
      followUps: [],
      fallback: false,
      at: Date.now(),
      ms: null
    }),
    items: [...((page && page.items) || []), raw],
    save: null
  }
  root.update(
    {
      _aiFly: {
        ...fly,
        open: true,
        focus: key,
        pages: page
          ? pages.map((entry) => (entry && entry.key === key ? local : entry))
          : [...pages, local]
      },
      _aiFlyWorkspace: {
        ...state,
        picker: null,
        status: 'added',
        error: null,
        seq: (state.seq || 0) + 1
      }
    },
    { preventFetch: true }
  )
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.assistantFlyPersist === 'function') this.call('assistantFlyPersist', root)
  return true
}

export function assistantFlyWorkspaceReturnShown(root) {
  const state = this.call('assistantFlyWorkspaceState', root)
  const fly = this.call('assistantFlySession', root)
  if (!state || !state.returnPending || !fly || !fly.open || root.assistantMode !== 'fly')
    return false
  return !!(root._canvasSlot || root._canvasSlotSecond) || !this.call('assistantFlyShown', root)
}

export function assistantFlyWorkspaceRemove(pageKey, itemKey) {
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  const fly = this.call('assistantFlySession', root)
  if (!state || !fly || root.assistantMode !== 'fly' || typeof root.update !== 'function')
    return false
  const pages = Array.isArray(fly.pages) ? fly.pages : []
  const page = pages.find((entry) => entry && entry.key === pageKey)
  if (!page || !/^fwl-/.test(page.key) || page.status !== 'ready') return false
  if (page.save === 'saving') {
    root.update(
      { _aiFlyWorkspace: { ...state, status: 'failed', error: 'saving' } },
      { preventFetch: true }
    )
    return false
  }
  const vocab = this.call('boardVocabularyFor', root)
  const checked = this.call('validateBoardSpec', { v: 1, items: page.items }, vocab)
  const item = checked.items.find((entry) => page.key + '-' + entry.key === itemKey)
  if (!item) return false
  const items = page.items.filter((raw) => {
    const result = this.call('validateBoardSpec', { v: 1, items: [raw] }, vocab)
    return !result.items.some((entry) => entry.key === item.key)
  })
  const next = items.length
    ? pages.map((entry) => (entry === page ? { ...page, items, save: null } : entry))
    : pages.filter((entry) => entry !== page)
  root.update(
    {
      _aiFly: {
        ...fly,
        pages: next,
        focus:
          !items.length && fly.focus === page.key
            ? (next[next.length - 1] || {}).key || null
            : fly.focus
      },
      _aiFlyWorkspace: { ...state, status: 'idle', error: null, seq: (state.seq || 0) + 1 }
    },
    { preventFetch: true }
  )
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.assistantFlyPersist === 'function') this.call('assistantFlyPersist', root)
  return true
}

export function assistantFlyWorkspaceReturn() {
  const root = this.state && this.state.root
  const state = this.call('assistantFlyWorkspaceState', root)
  const fly = this.call('assistantFlySession', root)
  if (
    !state ||
    !fly ||
    !fly.open ||
    root.assistantMode !== 'fly' ||
    String(fly.workspaceId) !== state.workspaceId ||
    !root.update
  )
    return false
  const path = state.originPath || fly.path
  if (typeof path !== 'string' || !/^\/(?!\/)[^\s\\]*$/.test(path)) return false
  const seq = (state.seq || 0) + 1
  const operationId = crypto.randomUUID()
  const slots = [root._canvasSlot || null, root._canvasSlotSecond || null]
  const hosts = root.__slotLiveHosts || {}
  const liveHosts = [hosts._canvasSlot, hosts._canvasSlotSecond]
  const fromPath = root.currentPath
  root.update(
    {
      _aiFlyWorkspace: {
        ...state,
        picker: null,
        status: 'returning',
        error: null,
        operationId,
        seq
      }
    },
    { preventFetch: true }
  )
  const current = () => {
    const live = this.call('assistantFlyWorkspaceState', root)
    const session = this.call('assistantFlySession', root)
    return live &&
      root.assistantMode === 'fly' &&
      session &&
      session.open &&
      live.workspaceId === state.workspaceId &&
      live.operationId === operationId &&
      live.seq === seq
      ? live
      : null
  }
  const cancel = (error) => {
    const live = current()
    if (!live) return false
    root.update(
      { _aiFlyWorkspace: { ...live, status: error ? 'failed' : 'idle', error: error || null } },
      { preventFetch: true }
    )
    return false
  }
  const finish = () => {
    const live = current()
    if (!live) return false
    if (
      root.currentPath !== fromPath ||
      (root._canvasSlot || null) !== slots[0] ||
      (root._canvasSlotSecond || null) !== slots[1]
    )
      return cancel('changed')
    const restore = () => {
      const latest = current()
      const session = this.call('assistantFlySession', root)
      if (!latest || !session || String(session.workspaceId) !== state.workspaceId) return false
      const latestHosts = root.__slotLiveHosts || {}
      if (
        (root._canvasSlot || null) !== slots[0] ||
        (root._canvasSlotSecond || null) !== slots[1] ||
        latestHosts._canvasSlot !== liveHosts[0] ||
        latestHosts._canvasSlotSecond !== liveHosts[1] ||
        session.path !== fly.path ||
        session.focus !== fly.focus
      )
        return cancel('changed')
      // The guarded close mirrors PageSlot's close reset. Suppress its one-shot
      // immersive landing reopen, because Fly is the destination of this close.
      if (root._canvasSlot) root._slotSkipReopenOnce = true
      root.update(
        {
          _canvasSlot: null,
          _canvasSlotSecond: null,
          _slotFullscreen: null,
          _slotHistSecond: null,
          _slotRailPrimary: false,
          _slotRailSecond: false,
          _slotPinned: null,
          _slotSplitIntent: false,
          _aiFly: { ...session, open: true, path, focus: state.originFocus || session.focus },
          _aiFlyWorkspace: {
            ...latest,
            returnPending: false,
            status: 'idle',
            error: null
          }
        },
        { preventFetch: true }
      )
      return true
    }
    try {
      if (root.currentPath !== path) {
        const moved = this.router(path, this.getRoot())
        if (moved && typeof moved.then === 'function') {
          return moved.then(
            (ok) =>
              ok === false
                ? cancel()
                : root.currentPath === path
                  ? restore()
                  : cancel('navigation'),
            () => cancel('navigation')
          )
        }
        if (moved === false || root.currentPath !== path) return cancel('navigation')
      }
      return restore()
    } catch (_) {
      return cancel('navigation')
    }
  }
  const fns = (this.context && this.context.functions) || {}
  let dirty = false
  try {
    dirty =
      typeof fns.hasUnsavedInput === 'function' &&
      ((slots[0] && this.call('hasUnsavedInput', hosts._canvasSlot)) ||
        (slots[1] && this.call('hasUnsavedInput', hosts._canvasSlotSecond)) ||
        (root.currentPath !== path && this.call('hasUnsavedInput', this.getRoot())))
  } catch (_) {
    dirty = true
  }
  if (!dirty) return finish()
  if (typeof fns.confirmDiscardChanges !== 'function') return cancel('navigation')
  return new Promise((resolve) => {
    this.call('confirmDiscardChanges', {
      onDiscard: () => resolve(finish()),
      onKeep: () => resolve(cancel())
    })
  })
}
